import * as React from 'react'

import { Box, Button, Form, InputCheckBox, Section, Text } from '@factorialco/deprecated-design-system'
import {
  AttendanceEnumsOvertimePoolUpdateFrequencyEnum,
  AttendanceOvertimePool,
} from 'generated/resources/graphql'
import { F0Link } from '@factorialco/f0-react'
import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import Select from 'modules/core/components/Select'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useForm } from 'modules/core/hooks/useForm'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'

import CreateOvertimeAllowanceModal from 'modules/attendance/components/SettingItemPage/TimeTracking/Compensation/Content/CreateOvertimeAllowanceModal'
import { useBulkUpdateOvertimePools } from 'modules/attendance/components/SettingItemPage/TimeTracking/hooks/useBulkUpdateOvertimePools'
import { useUpdateTimeTrackingSetting } from 'modules/attendance/components/SettingItemPage/TimeTracking/hooks/useUpdateTimeTrackingSetting'
// eslint-disable-next-line factorial/no-allowed-mobx-collections
import timeoffCompensations, {
  TimeoffCompensationsProvider,
} from 'modules/attendance/store/collections/timeoffCompensation'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'
import type BalanceCarryover from 'modules/attendance/store/models/BalanceCarryover'
import TimeoffCompensation from 'modules/timeoff/components/TimeoffCompensation'
import TimeoffCompensationForm from 'modules/timeoff/lib/TimeoffCompensationForm'

import {
  DEFAULT_UPDATE_FREQUENCY,
  getUpdateFrequencyOptions,
  updateFrequencyToEnum,
  validateCompensationPeriodCompatibility,
} from './utils'

export interface Props {
  attendanceSetting: AttendanceSetting
  timeoffCompensationEnabled: boolean
  balanceCarryover?: BalanceCarryover
  setBalanceCarryover?: (balanceCarryover: BalanceCarryover) => void
  hasExtraHourRules: boolean
  hasTimeOffExtraHourRules: boolean
  overtimePools: AttendanceOvertimePool[]
  hasLimitBalancePool: boolean
  hasManualAllocation: boolean
}

type FormValues = {
  attendanceEmployeesSettingId: number
  updateFrequency: AttendanceEnumsOvertimePoolUpdateFrequencyEnum
}

const NewOvertimeCompensation = ({
  attendanceSetting,
  timeoffCompensationEnabled,
  balanceCarryover,
  setBalanceCarryover,
  hasExtraHourRules,
  hasTimeOffExtraHourRules,
  overtimePools,
  hasLimitBalancePool,
  hasManualAllocation,
}: Props) => {
  const { navigation } = useNavigation()
  const { bulkUpdateOvertimePool, loading } = useBulkUpdateOvertimePools()
  const { updateSetting } = useUpdateTimeTrackingSetting()
  const [createAllowanceModalOpen, setCreateAllowanceModalOpen] = React.useState(false)
  const [validationError, setValidationError] = React.useState<string | undefined>()

  const allowManualAllocation = attendanceSetting.attributes.allow_manual_allocation ?? false

  const { running: updatingAllowManualAllocation, performAction: toggleAllowManualAllocation } =
    useAsyncAction(
      async () => {
        const newValue = !allowManualAllocation

        const result = await updateSetting({
          id: attendanceSetting.id,
          allowManualAllocation: newValue,
        })

        const handled = handleResult(result, () => undefined)

        await attendanceSetting.fetch()

        return handled
      },
      {
        successFlash: true,
        errorFlash: false
      }
    )

  const settingId = attendanceSetting.attributes.id
  const extraHoursTransferFrequency = attendanceSetting.extraHoursTransferFrequency
  const overtimePool = overtimePools.at(0)

  useAsyncData(
    () =>
      Promise.all([
        timeoffCompensations.fetch({
          data: {
            attendance_employees_setting_id: settingId,
          },
        }),
      ]),
    [settingId]
  )

  const currentTimeoffCompensation = timeoffCompensations.last()
  const formValues = currentTimeoffCompensation && currentTimeoffCompensation.toJS()
  const compensationForm = new TimeoffCompensationForm(formValues)

  const form = useForm<FormValues>({
    defaultValues: {
      attendanceEmployeesSettingId: attendanceSetting.id,
      updateFrequency: updateFrequencyToEnum(
        overtimePool?.updateFrequency ||
          balanceCarryover?.resetFrequency ||
          DEFAULT_UPDATE_FREQUENCY
      ),
    },
    onSubmit: async (data, { setErrors }) => {
      if (balanceCarryover) {
        const validation = validateCompensationPeriodCompatibility(
          data.updateFrequency,
          extraHoursTransferFrequency,
          balanceCarryover.resetFrequency
        )
        if (!validation.isValid) {
          setErrors({ updateFrequency: validation.errorMessage })
          return { success: false, errors: [] }
        }
      }

      const result = await bulkUpdateOvertimePool({
        attendanceEmployeesSettingId: data.attendanceEmployeesSettingId,
        updateFrequency: data.updateFrequency,
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success && handled.data) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        form.resetForm({
          values: {
            attendanceEmployeesSettingId: attendanceSetting.id,
            updateFrequency: updateFrequencyToEnum(
              handled?.data?.overtimePools?.[0]?.updateFrequency ||
                balanceCarryover?.resetFrequency ||
                DEFAULT_UPDATE_FREQUENCY
            ),
          },
        })
      }

      return handled
    },
  })

  const handleModalOnSubmit = () => {
    setCreateAllowanceModalOpen(false)
  }

  const handleFrequencyChange = (value: string) => {
    const updateFrequencyValue = updateFrequencyToEnum(value)
    if (balanceCarryover) {
      const validation = validateCompensationPeriodCompatibility(
        updateFrequencyValue,
        extraHoursTransferFrequency,
        balanceCarryover.resetFrequency
      )
      setValidationError(validation.errorMessage)
    }
    form.setFieldValue('updateFrequency', updateFrequencyValue)
  }

  const showTimeOffSettings = hasTimeOffExtraHourRules && timeoffCompensationEnabled
  const updateFrequencyIsDaily = form.values.updateFrequency == 'daily'
  const disableSubmitButton = !!validationError || loading

  return (
    <Section
      illustration="monety-bag"
      title={i18n.t('time_tracking.settings.compensation.title')}
      description={i18n.t('time_tracking.settings.compensation.description')}
    >
      <Box
        flexDirection="column"
        gap="s8"
        background="grey200"
        padding="s24"
        borderRadius={{
          all: 'abs008',
        }}
      >
        <Box gap="s4" paddingBottom="s8">
          <Text size="200" weight="semibold">
            {i18n.t('time_tracking.settings.compensation.compensation_period')}
          </Text>
          <Text size="200">{i18n.t('time_tracking.settings.compensation.period_description')}</Text>
        </Box>
        <Form onSubmit={() => form.handleSubmit()} padding={null}>
          <Select
            value={form.values.updateFrequency}
            options={getUpdateFrequencyOptions()}
            onChange={handleFrequencyChange}
          />
          {hasManualAllocation && (
            <Box flexDirection="column">
              <Box flexDirection="row" gap="s8" paddingBottom="s8">
                <InputCheckBox
                  checked={allowManualAllocation}
                  onClick={toggleAllowManualAllocation}
                  disabled={updatingAllowManualAllocation}
                />
                <Text size="200" weight="medium">
                  {i18n.t('time_tracking.settings.compensation.allow_manual_allocation')}
                </Text>
              </Box>
              <Text size="200">
                {i18n.t('time_tracking.settings.compensation.allow_manual_allocation_description')}
              </Text>
              <Box paddingTop="s8" alignItems="flexStart">
                <F0Link
                  href={navigation.settings.permissions.fullPath()}
                  variant="link"
                  target="_blank"
                >
                  {i18n.t('time_tracking.settings.compensation.manage_allocation_permissions')}
                </F0Link>
              </Box>
            </Box>
          )}

          {!updateFrequencyIsDaily && (
            <Box flexDirection="row" gap="s4">
              <Text size="200" weight="bold">
                {i18n.t('time_tracking.settings.balance.reset_frequency.note')}
              </Text>
              <Text size="200">
                {i18n.t('time_tracking.settings.balance.reset_frequency.period_foot_note')}
              </Text>
            </Box>
          )}
          {validationError && (
            <Box padding="s8">
              <Callout type="warning" title="" description={validationError} />
            </Box>
          )}
          {form.dirty && (
            <Form.Buttons>
              <Button disabled={disableSubmitButton} submit>
                {i18n.t('common.save')}
              </Button>
            </Form.Buttons>
          )}
        </Form>
      </Box>
      <Box paddingTop="s24">
        {showTimeOffSettings && (
          <TimeoffCompensationsProvider collection={timeoffCompensations}>
            <TimeoffCompensation
              attendanceSetting={attendanceSetting}
              compensationForm={compensationForm}
              balanceCarryover={balanceCarryover}
              setBalanceCarryover={setBalanceCarryover}
              hasExtraHourRules={hasExtraHourRules}
              hasLimitBalancePool={hasLimitBalancePool}
            />
            {createAllowanceModalOpen && (
              <CreateOvertimeAllowanceModal
                onSubmit={handleModalOnSubmit}
                onClose={() => setCreateAllowanceModalOpen(false)}
              />
            )}
          </TimeoffCompensationsProvider>
        )}
      </Box>
    </Section>
  )
}

export default observer(NewOvertimeCompensation)
